import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Map, { Layer, type MapRef, type MapLayerMouseEvent } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * Visor cliente · Paridad razonable con `huimilpan_elawan/visor` (prioridad clic
 * parcela > núcleo > AOI; semáforo vía overlay + estado vivo). Opcional fuera del
 * monorepo: basemap satélite y relieve 3D.
 */
import { AoiLayer } from './layers/AoiLayer';
import { ParcelasLayer } from './layers/ParcelasLayer';
import { NucleosLayer } from './layers/NucleosLayer';
import { UsoComunLayer } from './layers/UsoComunLayer';
import { ZonasParceladasLayer } from './layers/ZonasParceladasLayer';
import { AsentamientosLayer } from './layers/AsentamientosLayer';
import { MacroalineamientoLayer } from './layers/MacroalineamientoLayer';
import { MunicipiosLayer } from './layers/MunicipiosLayer';
import { DynamicCapaLayer } from './layers/DynamicCapaLayer';

import { useLiveParcelas } from './hooks/useLiveParcelas';
import { resolveDataSources, VIEWPORT_DEFAULT } from './data-sources';
import { fitMapToAreaOfInterest } from './fitMapToAoi';
import { COLOR } from './styles';
import { MapToolbar, BASEMAP_OPTIONS, MAPBOX_SATELLITE_STREETS, type BasemapId, type LayerToggle } from './MapToolbar';
import { CapaFeaturePopup, type CapaPopupData } from './popups/CapaFeaturePopup';
import type {
  CapaRender,
  CapasConfig,
  MapaParcelasProps,
  ParcelaFeatureMeta,
  ParcelaLiveState,
} from './types';
import { computeColindanciasSpatial } from './colindanciasSpatial';
import { hasLayer } from './mapboxSafe';
import { labelTipoPropi } from './tipoPropi';

const ESTILO_DEFAULT = MAPBOX_SATELLITE_STREETS;

/**
 * Modo "demo": activa TODAS las capas para presentaciones / demos comerciales.
 */
export const CAPAS_DEMO_FULL: CapasConfig = {
  aoi: true,
  aoiSanPedro: true,
  parcelas: { semaforo: true, firmado: true, irregular: true },
  nucleos: true,
  municipios: false,
  // ON desde 2026-06-10: Elawan entregó la traza LAAT 115 kV (deja de ser
  // placeholder bloqueante; ver build_macroalineamiento.py).
  macroalineamiento: true,
  asentamientos: false,
  // Uso común ON por default desde mig 0068: dejó de ser capa decorativa —
  // es unidad de gestión clickeable (captura, semáforo, expedientes).
  usoComun: true,
  zonasParceladas: false,
};

const LAYER_LABELS: Record<keyof CapasConfig, string> = {
  aoi: 'Área de interés',
  aoiSanPedro: 'San Pedro (unificado)',
  parcelas: 'Parcelas',
  nucleos: 'Núcleos agrarios',
  municipios: 'Municipios',
  macroalineamiento: 'Macroalineamiento · LAAT 115 kV',
  asentamientos: 'Asentamientos',
  usoComun: 'Uso común',
  zonasParceladas: 'Zonas parceladas',
};

export const MapaParcelas = forwardRef<MapRef, MapaParcelasProps>(function MapaParcelas(
  {
    modo: _modo,
    dataSources,
    centroInicial = VIEWPORT_DEFAULT.centro,
    zoomInicial = VIEWPORT_DEFAULT.zoom,
    capas = { aoi: true, parcelas: { semaforo: true } },
    filtros,
    parcelasState,
    parcelasGeoJSON,
    usoComunGeoJSON,
    capasInformativas,
    flyToParcelaTrigger,
    focusEstadoTrigger,
    onParcelaClick,
    onEjidoClick,
    mapboxToken,
    mapStyle = ESTILO_DEFAULT,
    showToolbar = true,
    toolbarTopOffset,
    fullscreenActive,
    onToggleFullscreen,
    autoFitAoi = true,
    selectedParcelaId,
    filterBarVisible = true,
    onToggleFilterBar,
    shapesOpacity = 1,
  },
  ref,
) {
  const token = mapboxToken ?? import.meta.env.VITE_PUBLIC_MAPBOX_TOKEN;

  // Ref interna del mapa para que MapToolbar pueda invocar APIs.
  const internalMapRef = useRef<MapRef>(null);
  /** El Map asigna la ref en un commit posterior; no capturar `.current` en el primer render. */
  useImperativeHandle(
    ref,
    () =>
      ({
        getMap: () => internalMapRef.current?.getMap?.() ?? null,
      }) as MapRef,
    [],
  );

  // ResizeObserver: cuando el contenedor del mapa cambia de tamaño (ej.
  // se abre la parcel-bar inferior, el split del wizard cambia ancho, el
  // user redimensiona la ventana), Mapbox NO detecta automáticamente el
  // cambio del DIV padre — solo escucha resize de window. Sin esto, el
  // canvas queda con dimensiones del primer mount y muestra solo una
  // porción del mapa (o se ve estirado). El ref del shell apunta al div
  // wrapper del Map; cualquier mutación de su size dispara map.resize().
  const shellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    if (typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      // Coalescear múltiples dispatches en el mismo frame (resize burst
      // cuando ambas dimensiones cambian a la vez).
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const map = internalMapRef.current?.getMap?.();
        if (map && typeof map.resize === 'function') {
          try { map.resize(); } catch { /* map destruido durante un re-render */ }
        }
      });
    });
    ro.observe(node);
    return () => {
      ro.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, []);

  // Capas: el toggle del MapToolbar permite ocultar/mostrar localmente, pero
  // el caller manda el estado base via `capas`. El state local solo guarda
  // overrides (claves apagadas por el usuario desde el toolbar).
  const [layerOverrides, setLayerOverrides] = useState<Partial<Record<keyof CapasConfig, boolean>>>({});
  const capasState: CapasConfig = useMemo(() => {
    const merged: CapasConfig = { ...capas };
    for (const [k, v] of Object.entries(layerOverrides)) {
      if (v === false && k === 'parcelas') merged.parcelas = false;
      else if (v === true && k === 'parcelas') merged.parcelas = capas.parcelas ?? { semaforo: true };
      else (merged as Record<string, unknown>)[k] = v;
    }
    return merged;
  }, [capas, layerOverrides]);

  // Capas informativas (overlay de referencia): override local de visibilidad
  // por capa_id (clave `capa:<id>` en el toolbar) sobre el `visible` de la DB.
  const [capaOverrides, setCapaOverrides] = useState<Record<string, boolean>>({});
  const [capaPopup, setCapaPopup] = useState<CapaPopupData | null>(null);
  const capasInformativasResolved = useMemo<CapaRender[]>(() => {
    return (capasInformativas ?? []).filter((c) => {
      const override = capaOverrides[c.id];
      return override != null ? override : c.visible !== false;
    });
  }, [capasInformativas, capaOverrides]);

  // Basemap controlado localmente: la app puede pasar `mapStyle` y MapToolbar lo override.
  const [basemapStyle, setBasemapStyle] = useState<string>(mapStyle);
  const [basemapId, setBasemapId] = useState<BasemapId>(() => inferBasemapId(mapStyle));
  useEffect(() => {
    setBasemapStyle(mapStyle);
    setBasemapId(inferBasemapId(mapStyle));
  }, [mapStyle]);

  // GeoJSON estático servido en /maps/<layer>.geojson. El caller puede
  // override por capa pasando `dataSources` (útil para mock/test).
  const sources = useMemo(() => ({ ...resolveDataSources(), ...dataSources }), [dataSources]);
  const filtrosResolved = useMemo(() => filtros ?? {}, [filtros]);

  /** Capas de parcela consultables en clic (semáforo encima del fill base cuando aplica).
   *  Paridad con `huimilpan_elawan/visor`: clic sobre parcela funciona en TODOS los modos
   *  (publico/cliente/edicion). Si `capas.parcelas === false` no hay capa que consultar. */
  const parcelInteractiveLayerIds = useMemo(() => {
    if (capasState.parcelas === false) return [];
    const po = typeof capasState.parcelas === 'object' ? capasState.parcelas : { semaforo: true as const };
    const ids: string[] = [];
    if (po.semaforo !== false) ids.push('parcelas-fill-estado');
    ids.push('parcelas-fill');
    return ids;
  }, [capasState]);

  /** Capas de uso común consultables en clic. Desde mig 0068 los polígonos
   *  de uso común tienen parcela_id (HUM-UC####) y son gestionables — el
   *  clic enruta por el MISMO onParcelaClick que parcelas. El overlay de
   *  semáforo (`uso-comun-fill-estado`) solo existe en modo enriquecido;
   *  el filtro hasLayer en handleClick tolera su ausencia. */
  const usoComunInteractiveLayerIds = useMemo(() => {
    if (!capasState.usoComun) return [];
    return ['uso-comun-fill-estado', 'uso-comun-fill'];
  }, [capasState.usoComun]);

  /** Capas informativas clickeables: las 3 sub-capas (fill/line/circle) por capa visible. */
  const capaInteractiveLayerIds = useMemo(() => {
    return capasInformativasResolved.flatMap((c) => [
      `capa-${c.id}-fill`, `capa-${c.id}-line`, `capa-${c.id}-circle`,
    ]);
  }, [capasInformativasResolved]);

  const interactiveLayerIds = useMemo(() => {
    const ids = [...parcelInteractiveLayerIds, ...usoComunInteractiveLayerIds];
    if (capasState.nucleos) ids.push('nucleos-fill');
    if (capasState.aoi !== false) ids.push('aoi-fill');
    if (capasState.aoiSanPedro) {
      ids.push('aoi-san-pedro-fill', 'aoi-san-pedro-ring');
    }
    // Capas informativas al final = menor prioridad de clic (son referencia,
    // debajo de parcelas).
    ids.push(...capaInteractiveLayerIds);
    // `municipios` solo dibuja outline (MunicipiosLayer) — no hay `municipios-fill`
    // que consultar. Sin esto, queryRenderedFeatures lanza error ruidoso en cada mousemove.
    return ids;
  }, [parcelInteractiveLayerIds, usoComunInteractiveLayerIds, capaInteractiveLayerIds, capasState]);

  /**
   * Click handler con **prioridad explicita** (alineado a huimilpan_elawan):
   *   1. parcela (fill semáforo si existe, luego fill base)
   *   2. uso común (unidad de gestión desde mig 0068 — más específica que el núcleo)
   *   3. nucleo agrario (fill)
   *   4. AOI (fill)
   *
   * Usa `queryRenderedFeatures` con la lista de capas interactivas para
   * que el primer hit en cada layer gane independientemente del z-order
   * que reportaria `e.features[0]` cuando dos layers se traslapan.
   */
  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const map = internalMapRef.current?.getMap();
      if (!map) return;
      // queryRenderedFeatures lanza error si alguna capa no existe (race con style.load
      // o capa apagada). Filtramos por las que mapbox ya tiene registradas.
      // `hasLayer` envuelve getLayer en try/catch porque Mapbox lanza
      // (no devuelve undefined) si `map.style` aún no inicializa o el map
      // fue removido — bug observado por Diego 2026-05-11.
      const layerIds = interactiveLayerIds.filter((id) => id && hasLayer(map, id));
      if (layerIds.length === 0) return;
      const hits = map.queryRenderedFeatures(e.point, { layers: layerIds });
      if (!hits.length) return;

      // Prioridad: capas de parcela (arriba en el mapa) > uso común > núcleo
      // > AOI > municipios
      const priority = [
        ...parcelInteractiveLayerIds,
        ...usoComunInteractiveLayerIds,
        'nucleos-fill',
        'aoi-san-pedro-fill',
        'aoi-san-pedro-ring',
        'aoi-fill',
        'municipios-fill',
        // Capas informativas: menor prioridad (referencia, debajo de todo).
        ...capaInteractiveLayerIds,
      ];
      let chosen: (typeof hits)[number] | null = null;
      for (const id of priority) {
        const found = hits.find((h) => h.layer?.id === id);
        if (found) { chosen = found; break; }
      }
      if (!chosen) return;
      const layerId = chosen.layer?.id;
      const props = (chosen.properties ?? {}) as Record<string, unknown>;

      if (
        (layerId === 'parcelas-fill' || layerId === 'parcelas-fill-estado')
        && props.parcela_id != null
      ) {
        const meta = buildParcelaFeatureMeta(props, e.lngLat.lng, e.lngLat.lat);
        // Si el GeoJSON no trae `colindancias` declaradas en propiedades,
        // derivarlas espacialmente: tocan = comparten ≥1 vértice; bearing
        // centroid→centroid clasifica en 4 sectores cardinales. Cero
        // dependencias externas (ver colindanciasSpatial.ts).
        // Caché simple: las parcelas adyacentes rara vez cambian; el cómputo
        // por click es aceptable (~5-50 ms gracias al pre-filtro bbox).
        if (!meta.colindancias && chosen.geometry) {
          const targetFeature: GeoJSON.Feature = {
            type: 'Feature',
            properties: { ...props },
            geometry: chosen.geometry as GeoJSON.Geometry,
          };
          const candidates = collectParcelaFeaturesForSpatial(
            map as unknown as MapboxGetSourceShim,
            parcelasGeoJSON,
          );
          if (candidates.length > 0) {
            const spatial = computeColindanciasSpatial(targetFeature, candidates);
            if (spatial) meta.colindancias = spatial;
          }
        }
        onParcelaClick?.(String(props.parcela_id), meta);
        return;
      }
      // Uso común: mismo callback que parcela (paridad de gestión, RF3).
      // Sin derivación espacial de colindancias — colindanciasSpatial opera
      // sobre la fuente de parcelas individuales y excluye no-parcelas
      // (fase 2 opcional, ver docs/ARCHITECTURE.md); las colindancias de
      // uso común se capturan manualmente en el paso 2 del wizard.
      if (layerId === 'uso-comun-fill' || layerId === 'uso-comun-fill-estado') {
        if (props.parcela_id == null) {
          // Típico: GeoJSON viejo en caché (pre-mig 0068, sin parcela_id) —
          // el clic no puede resolver la unidad. Warn en vez de silencio
          // para que soporte lo detecte; el fix de caché (NetworkFirst en SW
          // + no-cache en /maps/*, 2026-06-10) evita llegar aquí.
          // eslint-disable-next-line no-console
          console.warn('[sig-viewer] uso común sin parcela_id — ¿GeoJSON viejo en caché? Recarga la app.', props);
          return;
        }
        const meta = buildParcelaFeatureMeta(props, e.lngLat.lng, e.lngLat.lat);
        onParcelaClick?.(String(props.parcela_id), meta);
        return;
      }
      if (layerId === 'nucleos-fill' && props.nom_nucleo != null) {
        onEjidoClick?.(String(props.nom_nucleo));
        return;
      }
      // Capa informativa: abrir popup con los atributos del feature. El layer id
      // es `capa-<uuid>-{fill|line|circle}` — extraemos el uuid para el nombre.
      const capaMatch = /^capa-(.+)-(?:fill|line|circle)$/.exec(layerId ?? '');
      if (capaMatch) {
        const capa = capasInformativasResolved.find((c) => c.id === capaMatch[1]);
        setCapaPopup({
          lng: e.lngLat.lng,
          lat: e.lngLat.lat,
          capaNombre: capa?.nombre ?? 'Capa',
          featureNombre: typeof props.feature_nombre === 'string' ? props.feature_nombre : null,
          properties: props,
        });
      }
    },
    [interactiveLayerIds, parcelInteractiveLayerIds, usoComunInteractiveLayerIds, capaInteractiveLayerIds, capasInformativasResolved, onParcelaClick, onEjidoClick],
  );

  /** Cursor pointer cuando el mouse pasa sobre una capa interactiva. */
  const handleMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      const map = internalMapRef.current?.getMap();
      if (!map) return;
      const layerIds = interactiveLayerIds.filter((id) => id && hasLayer(map, id));
      if (layerIds.length === 0) return;
      const hits = map.queryRenderedFeatures(e.point, { layers: layerIds });
      const canvas = map.getCanvas();
      canvas.style.cursor = hits.length ? 'pointer' : '';
    },
    [interactiveLayerIds],
  );

  /** Auto-fit al AOI cuando cargan las features. Solo on first load. */
  const fittedRef = useRef(false);
  const handleSourceData = useCallback(
    (e: { sourceId?: string; isSourceLoaded?: boolean; sourceDataType?: string }) => {
      if (fittedRef.current) return;
      if (!autoFitAoi) return;
      const sid = e.sourceId;
      if (
        (sid !== 'aoi' && sid !== 'aoi_san_pedro')
        || !e.isSourceLoaded
      ) {
        return;
      }
      const map = internalMapRef.current?.getMap();
      if (!map) return;
      fitMapToAreaOfInterest(map);
      fittedRef.current = true;
    },
    [autoFitAoi],
  );

  /** Diagnóstico: log de errores del estilo (los Layer/Source que rechaza Mapbox). */
  useEffect(() => {
    const map = internalMapRef.current?.getMap();
    if (!map) return;
    const onError = (e: { error?: Error }) => {
      // eslint-disable-next-line no-console
      console.error('[sig-viewer] mapbox error:', e.error?.message ?? e);
    };
    map.on('error', onError);
    return () => {
      map.off('error', onError);
    };
  }, []);


  /** Resalta y centra cuando la app establece parcela desde URL/formulario (equipo, deep links).
   *  Re-dispara también cuando incrementa `flyToParcelaTrigger` para que el botón
   *  "Centrar" en ParcelaInfoBar funcione aunque la parcela ya esté seleccionada. */
  useEffect(() => {
    const id = selectedParcelaId?.trim();
    const mb = internalMapRef.current?.getMap();
    if (!id || !mb) return;
    const mbMap: mapboxgl.Map = mb;

    let cancelled = false;

    function fitToParcel() {
      if (cancelled || !mbMap.isStyleLoaded() || !mbMap.getSource('parcelas')) return;

      // querySourceFeatures sólo devuelve features dentro de tiles renderizados;
      // si la parcela está fuera del viewport actual, no la encuentra. Para
      // robustez usamos `parcelasGeoJSON` / `usoComunGeoJSON` (objetos
      // completos en memoria) cuando están disponibles, con fallback a
      // querySourceFeatures sobre ambas fuentes — los IDs HUM-UC#### viven
      // en la fuente `uso-comun`, no en `parcelas` (mig 0068).
      let geom: { type: string; coordinates: unknown } | undefined;
      let foundInUsoComun = false;
      for (const fc of [parcelasGeoJSON, usoComunGeoJSON]) {
        if (geom || !fc?.features) continue;
        const f = fc.features.find(
          (ff) => String((ff.properties ?? {}).parcela_id ?? '') === id,
        );
        if (f?.geometry) {
          geom = f.geometry as { type: string; coordinates: unknown };
          foundInUsoComun = fc === usoComunGeoJSON;
        }
      }
      if (!geom) {
        for (const sourceId of ['parcelas', 'uso-comun']) {
          if (geom || !mbMap.getSource(sourceId)) continue;
          const src = mbMap.getSource(sourceId) as { type?: string } | undefined;
          const sourceLayer = src?.type === 'vector' ? 'parcelas_unificado' : undefined;
          const qOpts = sourceLayer ? { sourceLayer } : undefined;
          const all = (
            mbMap.querySourceFeatures(sourceId, qOpts) as unknown as Array<{
              properties?: Record<string, unknown>;
              geometry?: { type: string; coordinates: unknown };
            }>
          ).filter(Boolean);
          geom = all.find((f) => String(f.properties?.parcela_id ?? '') === id)?.geometry;
          if (geom) foundInUsoComun = sourceId === 'uso-comun';
        }
      }
      if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) return;

      const bounds = new mapboxgl.LngLatBounds();

      type Ring = Array<[number, number]>;

      function extendCoords(ring: Ring) {
        for (const c of ring) bounds.extend(c);
      }

      if (geom.type === 'Polygon') {
        const rings = geom.coordinates as Ring[];
        for (const ring of rings) extendCoords(ring);
      } else {
        const polys = geom.coordinates as Ring[][];
        for (const poly of polys) {
          for (const ring of poly) extendCoords(ring);
        }
      }

      if (bounds.isEmpty()) return;

      mbMap.fitBounds(bounds, {
        padding: 80,
        duration: 800,
        maxZoom: 17,
        essential: true,
      });

      // Blink: pulsa el outline de selección para confirmar feedback visual.
      pulseLayer(
        mbMap,
        foundInUsoComun ? 'uso-comun-selection-outline' : 'parcelas-selection-outline',
        'line-width',
        [3.2, 7.5],
        3,
        220,
      );
    }

    const onIdleOnce = () => {
      fitToParcel();
      mbMap.off('idle', onIdleOnce);
    };

    mbMap.on('idle', onIdleOnce);

    const timer = window.setTimeout(fitToParcel, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      mbMap.off('idle', onIdleOnce);
    };
    // Intencional: NO depende de parcelasGeoJSON/usoComunGeoJSON — un refetch
    // de datos vivos no debe re-centrar el mapa sobre la selección actual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParcelaId, flyToParcelaTrigger, sources.parcelas]);

  /**
   * Auto-center + blink cuando el caller incrementa `focusEstadoTrigger`
   * (TabAvance click una tile del semáforo). Calcula el bbox unión de
   * todas las parcelas con `live_estado` igual al filtro activo y fitBounds.
   */
  useEffect(() => {
    if (focusEstadoTrigger == null) return;
    const mb = internalMapRef.current?.getMap();
    if (!mb) return;
    const estados = filtros?.estado;
    if (!estados || estados.length === 0) return;

    let cancelled = false;

    function focusEstado() {
      if (cancelled || !mb || !mb.isStyleLoaded() || !mb.getSource('parcelas')) return;
      const src = mb.getSource('parcelas') as { type?: string } | undefined;
      const sourceLayer = src?.type === 'vector' ? 'parcelas_unificado' : undefined;
      const qOpts = sourceLayer ? { sourceLayer } : undefined;

      // querySourceFeatures con filter complejo (match + coalesce) puede
      // fallar silencioso en mapbox-gl 3.x con sources GeoJSON. Approach
      // robusto: traer todas y filtrar en JS contra properties.live_estado.
      const all = mb.querySourceFeatures('parcelas', qOpts) as unknown as Array<{
        properties?: Record<string, unknown>;
        geometry?: { type: string; coordinates: unknown };
      }>;
      const features = all.filter((f) => {
        const e = f.properties?.live_estado as string | undefined;
        return e != null && (estados as string[]).includes(e);
      });
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[sig-viewer] focusEstado:', estados, 'matched=', features.length, '/', all.length);
      }

      const bounds = new mapboxgl.LngLatBounds();
      type Ring = Array<[number, number]>;
      const extendCoords = (ring: Ring) => { for (const c of ring) bounds.extend(c); };

      let count = 0;
      for (const f of features) {
        const geom = f.geometry;
        if (!geom) continue;
        if (geom.type === 'Polygon') {
          for (const ring of geom.coordinates as Ring[]) extendCoords(ring);
          count++;
        } else if (geom.type === 'MultiPolygon') {
          for (const poly of geom.coordinates as Ring[][]) {
            for (const ring of poly) extendCoords(ring);
          }
          count++;
        }
      }
      if (count === 0 || bounds.isEmpty()) return;

      mb.fitBounds(bounds, { padding: 80, duration: 800, maxZoom: 16, essential: true });
      // Blink en el layer del semaforo (fill-estado) — opacity pulse.
      pulseLayer(mb, 'parcelas-fill-estado', 'fill-opacity', [0.7, 0.25], 3, 220);
    }

    const onIdleOnce = () => { focusEstado(); mb.off('idle', onIdleOnce); };
    mb.on('idle', onIdleOnce);
    const t = window.setTimeout(focusEstado, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      mb.off('idle', onIdleOnce);
    };
    // intencional: solo el trigger dispara; cambiar filtros sin trigger no centra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusEstadoTrigger]);

  const handleLayerToggle = useCallback(
    (key: string, next: boolean) => {
      if (key.startsWith('capa:')) {
        const id = key.slice('capa:'.length);
        setCapaOverrides((prev) => ({ ...prev, [id]: next }));
        return;
      }
      setLayerOverrides((prev) => ({ ...prev, [key as keyof CapasConfig]: next }));
    },
    [],
  );

  const handleBasemapChange = useCallback((id: BasemapId, styleUrl: string) => {
    setBasemapStyle(styleUrl);
    setBasemapId(id);
  }, []);

  const layerToggles: LayerToggle[] = useMemo(() => {
    const keys: (keyof CapasConfig)[] = [
      'aoi', 'aoiSanPedro', 'parcelas', 'nucleos', 'municipios',
      'macroalineamiento', 'asentamientos', 'usoComun', 'zonasParceladas',
    ];
    const staticToggles: LayerToggle[] = keys.map((k) => ({
      key: k,
      label: LAYER_LABELS[k],
      enabled:
        k === 'parcelas' ? capasState.parcelas !== false : Boolean(capasState[k]),
    }));
    // Capas informativas dinámicas (canal abierto): un toggle por capa.
    const capaToggles: LayerToggle[] = (capasInformativas ?? []).map((c) => {
      const ov = capaOverrides[c.id];
      return { key: `capa:${c.id}`, label: c.nombre, enabled: ov != null ? ov : c.visible !== false };
    });
    return [...staticToggles, ...capaToggles];
  }, [capasState, capasInformativas, capaOverrides]);

  if (!token) {
    return (
      <div
        className="sig-map-shell"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0,
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-dim)',
          fontSize: 13,
          zIndex: 0,
        }}
      >
        Falta VITE_PUBLIC_MAPBOX_TOKEN.
      </div>
    );
  }

  return (
    <div
      ref={shellRef}
      className="sig-map-shell"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        flex: '1 1 auto',
        zIndex: 0,
      }}
    >
      <Map
        ref={internalMapRef}
        mapboxAccessToken={token}
        initialViewState={{ longitude: centroInicial[0], latitude: centroInicial[1], zoom: zoomInicial }}
        mapStyle={basemapStyle}
        interactiveLayerIds={interactiveLayerIds}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onSourceData={handleSourceData}
        // Necesario para exportar el mapa como PNG: sin esto, WebGL limpia el
        // backbuffer al finalizar cada frame y `map.getCanvas()` devuelve un
        // canvas vacío al copiarlo. Trade-off documentado por Mapbox: baja
        // levísima de FPS en mapas muy grandes; imperceptible en este visor.
        preserveDrawingBuffer
        // Bloquea telemetria de Mapbox (events.mapbox.com): ruido en consola
        // cuando Safari suspende la pestaña + privacidad (no compartimos
        // métricas de uso del cliente con Mapbox). Cualquier otra request
        // pasa sin cambios.
        transformRequest={(url) => {
          if (url.includes('events.mapbox.com')) return { url: '' };
          return { url };
        }}
        style={{ width: '100%', height: '100%' }}
        cursor="grab"
      >
        <LiveStateInjector parcelasState={parcelasState} />
        {/* Orden: las parcelas SIEMPRE quedan arriba (las pintamos al final).
            Asi ningun fill de otra capa las puede tapar. */}
        {capasState.aoi !== false && <AoiLayer url={sources.aoi} />}
        {capasState.aoiSanPedro && (
          <AoiLayer
            url={sources.aoi_san_pedro}
            sourceId="aoi_san_pedro"
            fillLayerId="aoi-san-pedro-fill"
            outlineLayerId="aoi-san-pedro-outline"
            showOutline={false}
            fillColor={COLOR.aoi_san_pedro}
            fillOpacity={0.08}
          />
        )}
        {capasState.zonasParceladas && <ZonasParceladasLayer url={sources.zonas_parceladas} />}
        {capasState.usoComun && (
          <UsoComunLayer
            url={sources.uso_comun}
            geojson={usoComunGeoJSON}
            filtros={filtrosResolved}
            highlightParcelaId={selectedParcelaId?.trim() || undefined}
          />
        )}
        {capasState.asentamientos && <AsentamientosLayer url={sources.asentamientos} />}
        {capasState.nucleos && <NucleosLayer url={sources.nucleos} />}
        {capasState.municipios && <MunicipiosLayer url={sources.municipios} />}
        {capasState.macroalineamiento && <MacroalineamientoLayer url={sources.macroalineamiento} />}
        {/* Capas informativas (referencia): debajo de parcelas, encima del basemap.
            El orden de montaje (antes de ParcelasLayer) las deja por debajo. */}
        {capasInformativasResolved.map((c) => (
          <DynamicCapaLayer key={c.id} capa={c} opacityMul={shapesOpacity} />
        ))}
        {/* Parcelas al final = arriba en mapbox. Centro de analisis. */}
        {capasState.parcelas !== false && (
          <ParcelasLayer
            url={sources.parcelas}
            geojson={parcelasGeoJSON}
            opciones={typeof capasState.parcelas === 'object' ? capasState.parcelas : { semaforo: true }}
            filtros={filtrosResolved}
            highlightParcelaId={selectedParcelaId?.trim() || undefined}
            shapesOpacity={shapesOpacity}
          />
        )}
        {capasState.aoiSanPedro && (
          <Layer
            id="aoi-san-pedro-ring"
            type="line"
            source="aoi_san_pedro"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{
              // Literal para evitar caché de estilo con build viejo (antes violeta).
              'line-color': '#1F556E',
              'line-width': 3.4,
              'line-opacity': 0.96,
            }}
          />
        )}
        {capaPopup && (
          <CapaFeaturePopup data={capaPopup} onClose={() => setCapaPopup(null)} />
        )}
      </Map>

      {showToolbar && (
        <MapToolbar
          mapRef={internalMapRef}
          layers={layerToggles}
          onLayerToggle={handleLayerToggle}
          currentBasemap={basemapId}
          onBasemapChange={handleBasemapChange}
          fullscreenActive={fullscreenActive}
          onToggleFullscreen={onToggleFullscreen}
          filterBarVisible={filterBarVisible}
          onToggleFilterBar={onToggleFilterBar}
          topOffset={toolbarTopOffset}
        />
      )}
    </div>
  );
});

function LiveStateInjector({ parcelasState }: { parcelasState?: ParcelaLiveState[] }) {
  useLiveParcelas(parcelasState);
  return null;
}

function inferBasemapId(styleUrl: string): BasemapId {
  if (styleUrl.includes('satellite')) return 'satellite';
  if (styleUrl.includes('streets'))   return 'streets';
  if (styleUrl.includes('light'))     return 'light';
  return 'dark';
}

function parseAreaHaProp(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Subset de la API de mapbox-gl Map que necesitamos para extraer features
 * de la fuente de parcelas. Mantenido inline para evitar acoplar a las
 * tipings de mapbox-gl (que a veces difieren entre versiones).
 */
interface MapboxGetSourceShim {
  getSource: (id: string) => { type?: string } | undefined;
  querySourceFeatures: (id: string, opts?: unknown) => unknown[];
}

/**
 * Reúne los Features de parcelas para alimentar a `computeColindanciasSpatial`.
 * Prefiere la colección completa (`parcelasGeoJSON?.features`) cuando el
 * caller la pasa por prop — más confiable porque no depende del viewport.
 * Fallback a `querySourceFeatures` (solo tiles renderizados) cuando el
 * GeoJSON se carga internamente; en zoom típico de Captura los vecinos
 * inmediatos siempre están en pantalla, así que el fallback funciona.
 */
function collectParcelaFeaturesForSpatial(
  map: MapboxGetSourceShim | null | undefined,
  parcelasGeoJSON?: GeoJSON.FeatureCollection,
): GeoJSON.Feature[] {
  if (parcelasGeoJSON?.features?.length) {
    return parcelasGeoJSON.features as GeoJSON.Feature[];
  }
  if (!map) return [];
  try {
    const src = map.getSource('parcelas');
    if (!src) return [];
    const sourceLayer = src.type === 'vector' ? 'parcelas_unificado' : undefined;
    const qOpts = sourceLayer ? { sourceLayer } : undefined;
    const all = map.querySourceFeatures('parcelas', qOpts) as Array<{
      properties?: Record<string, unknown>;
      geometry?: GeoJSON.Geometry;
    }>;
    return all
      .filter((f): f is { properties: Record<string, unknown>; geometry: GeoJSON.Geometry } =>
        !!f && !!f.geometry,
      )
      .map((f) => ({
        type: 'Feature' as const,
        properties: f.properties,
        geometry: f.geometry,
      }));
  } catch {
    return [];
  }
}

function buildParcelaFeatureMeta(
  props: Record<string, unknown>,
  lng: number,
  lat: number,
): ParcelaFeatureMeta {
  const raw = { ...props };
  const ranTipo = raw.tipo_propi;
  return {
    parcela_id: String(raw.parcela_id ?? ''),
    num_parcela: raw.num_parcela as string | number | undefined,
    poligono: raw.poligono as string | number | undefined,
    zona: raw.zona as string | number | undefined,
    nom_nucleo: raw.nom_nucleo as string | undefined,
    municipio: raw.municipio as string | undefined,
    area_ha: parseAreaHaProp(raw.area_ha),
    tipo_propi: ranTipo != null ? String(ranTipo) : undefined,
    tipo_parcela: labelTipoPropi(raw.tipo_parcela, ranTipo),
    lng,
    lat,
    colindancias: parseColindanciasProp(raw.colindancias),
    raw,
  };
}

/**
 * Lee `properties.colindancias` del GeoJSON. Espera un objeto con llaves
 * `norte | sur | oriente | poniente`, cada una `string | null | undefined`.
 * Si el feature no incluye el campo (caso actual del pipeline RAN), devuelve
 * `undefined` y la UI muestra empty-state. Tolera shapes parciales — si solo
 * hay 2 de las 4 llaves se conservan.
 */
function parseColindanciasProp(value: unknown): ParcelaFeatureMeta['colindancias'] {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const obj = value as Record<string, unknown>;
  const pick = (k: string): string | null | undefined => {
    const v = obj[k];
    if (v === null) return null;
    if (typeof v === 'string') {
      const t = v.trim();
      return t === '' ? null : t;
    }
    return undefined;
  };
  const out = {
    norte: pick('norte'),
    sur: pick('sur'),
    oriente: pick('oriente'),
    poniente: pick('poniente'),
  };
  // Si las 4 vienen `undefined` consideramos que el campo no aporta nada.
  const algunaDefinida = Object.values(out).some((v) => v !== undefined);
  return algunaDefinida ? out : undefined;
}

/**
 * Anima un paint property de una layer entre dos valores N veces como blink.
 * Uso: confirmar al user que la acción de "centrar/seleccionar" surtió
 * efecto. Al final restaura el valor original.
 *
 * - `prop` debe ser numérico (line-width, fill-opacity, etc.).
 * - `cycles` veces ida y vuelta entre `from` y `to`.
 * - `intervalMs` ms entre cada cambio.
 *
 * Si la layer no existe (race con style.load), no hace nada.
 */
function pulseLayer(
  map: mapboxgl.Map,
  layerId: string,
  prop: string,
  values: [number, number],
  cycles = 3,
  intervalMs = 220,
): void {
  if (!hasLayer(map, layerId)) return;
  // Cast a never: el union literal de mapbox-gl tiene 300+ paint props;
  // aquí solo lo usamos con 'fill-opacity', 'line-width', 'line-opacity'
  // (todos numéricos). El cast es seguro.
  const propTyped = prop as never;
  const original = map.getPaintProperty(layerId, propTyped);
  let i = 0;
  const total = cycles * 2;
  const tick = () => {
    if (!hasLayer(map, layerId)) return;
    const v = i % 2 === 0 ? values[1] : values[0];
    try {
      map.setPaintProperty(layerId, propTyped, v as never);
    } catch { /* race con style change — ignore */ }
    i++;
    if (i >= total) {
      try {
        map.setPaintProperty(layerId, propTyped, original as never);
      } catch { /* idem */ }
      return;
    }
    window.setTimeout(tick, intervalMs);
  };
  tick();
}

// Re-export para que el caller pueda usarlas sin re-importar de subpaths.
export { BASEMAP_OPTIONS };
